// src/lib/quiz/outcomes.ts
/**
 * The outcome quiz tally, shared by every renderer and by the server.
 *
 * Phase 1 of docs/outcome-quiz_plan.md. Decision 0.1 is tag counting: each
 * answer choice carries the ids of the outcomes it counts toward, and the
 * highest tally wins. That evaluates no conditions, so this module does not
 * touch src/lib/utils/logicEngine.ts and needs no rule engine (the plan's
 * "Rule engines we already have" section is the checked version of that call).
 *
 * This file is pure on purpose: no React, no Prisma, no fetch, no DOM. Its
 * callers import it: LiveForm (the hosted page and the marketing popup island),
 * the vanilla kiroku-form.ts embed (decision 0.2), the Preact component in
 * kiroku-embed.ts (the react-mode embed and the popup on a customer's own
 * site), and the submission handler, which recomputes the result because
 * anything posted from a customer's page is visitor-controlled.
 * tests/guard/quiz-renderers-share-one-evaluator.test.ts fails if a renderer
 * grows a second tally, and now also if one of the three stops calling this.
 *
 * Corrected 2026-09-03: this comment said LiveForm also served the react-mode
 * embed. It does not. kiroku-embed.ts:1063 builds its own Preact component, so
 * the embed and the popup on a customer's site are a third renderer, and it
 * calls this module too as of plan task 6.4.
 *
 * The tripwire from the plan, repeated here because this is where it will be
 * tempting: the first time an outcome needs a condition that is not a tag
 * count, stop and revisit that section rather than adding an operator.
 */
import type {
  FieldOption,
  FormOutcome,
  FormOutcomeBand,
  FormOutcomeCta,
  QuizResult,
} from "@/lib/types";

/**
 * The minimum shape the tally reads. Written structurally rather than as
 * `TypedForm` so the vanilla embed, which holds a plain JSON form from the API,
 * and the server, which holds a Prisma row, can both pass what they already
 * have without a conversion step.
 */
export type QuizFormLike = {
  settings?: { outcomes?: FormOutcome[] | null } | null;
  fields?: QuizFieldLike[] | null;
};

export type QuizFieldLike = {
  name: string;
  /**
   * The field type, read by `maxReachableScore` alone: a checkbox group can
   * score every one of its boxes and a radio can only score one, which is the
   * difference between the highest total a form can reach and a number twice
   * that. Optional because the tally never needs it, and an absent type is
   * assumed to allow every box (see that function for why that direction).
   */
  type?: string | null;
  /**
   * What the author called this question. The tally never reads it; the save
   * rule in ./reachability.ts names it back to the author when it refuses a
   * half-tagged quiz.
   */
  label?: string | null;
  options?: FieldOption[] | null;
};

/** The answers as both renderers hold them: keyed by field name. */
export type QuizAnswers = Record<string, unknown>;

/**
 * The outcomes a form actually declares, in author order, with malformed
 * entries dropped. A settings blob can be hand-edited and it lands on a
 * customer's page, so a bad entry loses its vote rather than taking the form
 * down with it.
 */
export function getFormOutcomes(
  form: QuizFormLike | null | undefined,
): FormOutcome[] {
  const declared = form?.settings?.outcomes;
  if (!Array.isArray(declared)) return [];

  const seen = new Set<string>();
  return declared.filter((outcome): outcome is FormOutcome => {
    if (!outcome || typeof outcome !== "object") return false;
    const { id } = outcome as FormOutcome;
    if (typeof id !== "string" || id === "") return false;
    if (seen.has(id)) return false; // first declaration wins, per author order
    seen.add(id);
    return true;
  });
}

/** True when this form ends on a computed result rather than `successMessage`. */
export function isOutcomeQuiz(form: QuizFormLike | null | undefined): boolean {
  return getFormOutcomes(form).length > 0;
}

/**
 * Normalise one answer to the set of option values it selected. A select or
 * radio gives a string, a checkbox group gives an array, and an option's value
 * may be a number or a boolean while the DOM always posts a string, so both
 * sides are compared as strings.
 */
function selectedValues(answer: unknown): Set<string> {
  if (answer === null || answer === undefined) return new Set();
  const raw = Array.isArray(answer) ? answer : [answer];
  const values = new Set<string>();
  for (const value of raw) {
    if (value === null || value === undefined) continue;
    if (typeof value === "object") continue; // a File, never a choice
    values.add(String(value));
  }
  return values;
}

/**
 * The fields worth looking at: an object with a `name`, whatever else the
 * caller happens to be holding. A hand-edited fields array can contain a null.
 */
function quizFields(form: QuizFormLike | null | undefined): QuizFieldLike[] {
  const fields = Array.isArray(form?.fields) ? form!.fields! : [];
  return fields.filter(
    (field): field is QuizFieldLike => !!field && typeof field === "object",
  );
}

/**
 * The choices the visitor picked on one field, in the author's order.
 *
 * Shared by the tally and the total (plan 5.2) so the two selection models
 * read the same answers. Two functions deciding separately what "chosen"
 * means is the drift
 * tests/guard/quiz-renderers-share-one-evaluator.test.ts exists to stop,
 * one module in from where that guard can see it.
 */
function chosenOptions(
  field: QuizFieldLike,
  answers: QuizAnswers | null | undefined,
): FieldOption[] {
  const options = field.options;
  if (!Array.isArray(options) || options.length === 0) return [];

  const chosen = selectedValues(answers?.[field.name]);
  if (chosen.size === 0) return [];

  return options.filter(
    (option): option is FieldOption =>
      !!option &&
      typeof option === "object" &&
      chosen.has(String(option.value)),
  );
}

/**
 * Whether a choice scores at all: does it carry a tag naming an outcome this
 * form still declares?
 *
 * The same question `tallyScores` asks through `scores.get(id)`, and the same
 * one ./reachability.ts asks when it certifies a quiz as scoring. A choice
 * that counted toward a tally but not toward a total, or the other way round,
 * would put the two selection models on different data.
 */
function scoresSomething(
  option: FieldOption,
  declared: ReadonlySet<string>,
): boolean {
  const tags = option.outcomeIds;
  if (!Array.isArray(tags)) return false;
  return tags.some((id) => typeof id === "string" && declared.has(id));
}

/**
 * What one chosen choice is worth to each outcome it is tagged with.
 *
 * 1 unless the author said otherwise, which is what tag counting added before
 * weights existed, so a quiz with no `points` anywhere tallies exactly as it
 * always has (plan 5.1). A weight of 0 is honoured: "Not at all" is a real
 * scorecard answer worth nothing, and it still differs from an untagged choice
 * because the question counts as answered.
 *
 * Anything unusable reads as 1 rather than as 0 or as a thrown error. The
 * settings blob is hand-editable and lands on a customer's page, so `"3"`,
 * `null`, `NaN` and `Infinity` have to mean something, and a tagged choice
 * that silently scored nothing would make ./reachability.ts's save rule false:
 * that rule certifies a quiz as scoring by reading the same tags. A negative
 * is unusable for the same reason and one more: `tallyOutcomes` reads a total
 * of 0 as "nothing scored", so a weight below 0 would let a real answer land
 * on the default outcome. Negative weights are a disqualification feature
 * nobody has asked for; 5.2 is where an author gets told at save time.
 *
 * Exported because a band has to know the highest total a form can reach
 * (plan 5.2) and a second copy of this rule is exactly the drift the Phase 1
 * guard exists to stop.
 */
export function optionPoints(option: FieldOption | null | undefined): number {
  const points = option?.points;
  if (typeof points !== "number") return 1;
  if (!Number.isFinite(points) || points < 0) return 1;
  return points;
}

/**
 * Count the tags on the chosen answers, weighted by each choice's `points`.
 *
 * A choice counts at most once toward each outcome however often it is tagged,
 * so a duplicated tag cannot out-vote two real answers. A tag naming an
 * outcome the form does not declare counts for nothing, which is what makes
 * deleting an outcome safe at submit time.
 */
export function tallyScores(
  form: QuizFormLike | null | undefined,
  answers: QuizAnswers | null | undefined,
): Map<string, number> {
  const scores = new Map<string, number>();
  for (const outcome of getFormOutcomes(form)) scores.set(outcome.id, 0);
  if (scores.size === 0) return scores;

  for (const field of quizFields(form)) {
    for (const option of chosenOptions(field, answers)) {
      const tags = option.outcomeIds;
      if (!Array.isArray(tags)) continue;
      // Read once per choice, not once per tag: the weight is the choice's,
      // and the Set below is what stops a duplicated tag banking it twice.
      const weight = optionPoints(option);
      for (const id of new Set(tags)) {
        const current = scores.get(id);
        if (current === undefined) continue; // tag names no declared outcome
        scores.set(id, current + weight);
      }
    }
  }

  return scores;
}

/**
 * One total for the whole quiz, which is what a score band reads.
 *
 * The second selection model (plan 5.2, research §11): tag counting asks
 * "which result did they choose most often?" and reads a per-result tally,
 * while a band asks "how did they score overall?" and reads one number. A
 * readiness scorecard needs the second, because every choice counts toward
 * the same thing and it is the size of the total that decides what to say.
 *
 * A choice is counted once at its own weight however many results it is
 * tagged with, matching `optionPoints`'s rule that the weight belongs to the
 * choice. A choice that scores nothing in `tallyScores` scores nothing here.
 *
 * Never negative, because `optionPoints` refuses a negative weight, which is
 * what lets the save rule treat 0 as the floor of the reachable range.
 */
export function totalScore(
  form: QuizFormLike | null | undefined,
  answers: QuizAnswers | null | undefined,
): number {
  const declared = new Set(getFormOutcomes(form).map((outcome) => outcome.id));
  if (declared.size === 0) return 0;

  let total = 0;
  for (const field of quizFields(form)) {
    for (const option of chosenOptions(field, answers)) {
      if (!scoresSomething(option, declared)) continue;
      total += optionPoints(option);
    }
  }
  return total;
}

/**
 * The field types on which a visitor can pick exactly one choice. Everything
 * else with options is treated as a checkbox group, which can score all of
 * them.
 */
const SINGLE_CHOICE_TYPES = new Set(["radio", "select"]);

/**
 * The highest total this form can produce, so the save rule can insist the
 * bands reach it (plan 5.2).
 *
 * The floor is not computed and is always 0: field logic can hide any question
 * (`evaluateFieldVisibility`), and an optional question can be skipped, so
 * "answered nothing that scores" is reachable on every form.
 *
 * An unknown field type counts as multi-choice on purpose. Over-counting the
 * ceiling costs an author a wider top band; under-counting leaves a total a
 * visitor can actually reach with no result covering it, which is the hole
 * this whole rule exists to prevent.
 */
export function maxReachableScore(
  form: QuizFormLike | null | undefined,
): number {
  const declared = new Set(getFormOutcomes(form).map((outcome) => outcome.id));
  if (declared.size === 0) return 0;

  let ceiling = 0;
  for (const field of quizFields(form)) {
    const options = Array.isArray(field.options) ? field.options : [];
    const weights = options
      .filter(
        (option): option is FieldOption =>
          !!option &&
          typeof option === "object" &&
          scoresSomething(option, declared),
      )
      .map(optionPoints);
    if (weights.length === 0) continue;

    const type =
      typeof field.type === "string" ? field.type.trim().toLowerCase() : "";
    ceiling += SINGLE_CHOICE_TYPES.has(type)
      ? Math.max(...weights)
      : weights.reduce((sum, weight) => sum + weight, 0);
  }
  return ceiling;
}

/**
 * The range one outcome answers for, or `null` when it declares none it can
 * use. Both ends are inclusive and both are required: a settings blob is
 * hand-editable and lands on a customer's page, so half a range loses its
 * range rather than the visitor's result.
 */
export function outcomeBand(
  outcome: FormOutcome | null | undefined,
): FormOutcomeBand | null {
  const band = outcome?.band;
  if (!band || typeof band !== "object") return null;
  const { min, max } = band as FormOutcomeBand;
  if (typeof min !== "number" || !Number.isFinite(min)) return null;
  if (typeof max !== "number" || !Number.isFinite(max)) return null;
  if (max < min) return null;
  return { min, max };
}

/**
 * Whether the author meant this outcome to have a band, well-formed or not.
 *
 * Separate from `outcomeBand` because the save rule has to tell "no band" from
 * "a band I could not read": the first is an ordinary tag-counting quiz and
 * the second is a typo the author needs to hear about.
 */
export function declaresBand(outcome: FormOutcome | null | undefined): boolean {
  return outcome?.band !== undefined && outcome?.band !== null;
}

/** The usable bands a form declares, in author order. */
export function scoreBands(
  form: QuizFormLike | null | undefined,
): Array<{ outcome: FormOutcome; band: FormOutcomeBand }> {
  const bands: Array<{ outcome: FormOutcome; band: FormOutcomeBand }> = [];
  for (const outcome of getFormOutcomes(form)) {
    const band = outcomeBand(outcome);
    if (band) bands.push({ outcome, band });
  }
  return bands;
}

/**
 * The outcome a total lands on: the band with the highest floor at or below
 * it.
 *
 * `max` decides nothing here, and that is the point. The save rule reads it,
 * so an author is told at save time about a gap, an overlap or a top band that
 * stops short. By the time a visitor has finished the form the only useful
 * behaviour left is to answer, so selection is hole-free by construction: a
 * total above every band lands in the top one, and a fractional total between
 * two integer bands lands in the one below it rather than nowhere.
 *
 * A total below every band is the one case with nothing sensible below it, and
 * it falls to the author's default outcome, exactly as a tally of 0 does.
 * Equal floors leave the earlier outcome in place, the same tie-break as
 * argmax, so this comparison must never become `>=`.
 */
function bandWinner(
  bands: Array<{ outcome: FormOutcome; band: FormOutcomeBand }>,
  outcomes: FormOutcome[],
  total: number,
): FormOutcome {
  let winner: FormOutcome | null = null;
  let floor = -Infinity;
  for (const { outcome, band } of bands) {
    if (band.min > total) continue;
    if (band.min > floor) {
      winner = outcome;
      floor = band.min;
    }
  }
  return (
    winner ??
    outcomes.find((outcome) => outcome.isDefault === true) ??
    outcomes[0]
  );
}

/**
 * The href a renderer may put on an outcome's call to action, or `null`.
 *
 * Lives here rather than in a renderer because Phase 6 gives the vanilla embed
 * the same screen, and a scheme check that exists in one renderer and not the
 * other is the drift guard 5.2 was written for.
 *
 * Two jobs. It refuses anything outside the safelist, so a `javascript:` or
 * `data:` URL pasted into the authoring field renders no button instead of an
 * executable one on the customer's page. And it accepts a bare host, because
 * "book.example.com/call" is what a non-technical owner types (CLAUDE.md §0a)
 * and dropping their button over a missing prefix is our bug, not theirs.
 */
const SAFE_CTA_SCHEME = /^(?:https?:|mailto:|tel:)/i;
const BARE_HOST = /^[\w-]+(?:\.[\w-]+)+(?:[/:?#]|$)/;

/**
 * The "no" beside an outcome's CTA, shared by all three renderers.
 *
 * An outcome that asks for something ("Book a demo", "Start my audit") is an
 * offer, and the screen used to carry only the yes: the result replaced the
 * form, the popup's decline row went with the form, and on the hosted page
 * there was nothing to press at all. Reported 2026-09-17 on a newsletter
 * popup whose two outcomes both ended in a booking link.
 *
 * Rendered only when the outcome has a CTA, because it is the answer to that
 * button and means nothing under a plain result. In a popup it closes the
 * modal; inline and on the hosted page it swaps the result for the ordinary
 * thank-you, so the visitor still gets a receipt for what they sent.
 *
 * Same neutral wording as the popup's own decline (`DEFAULT_DECLINE_TEXT` in
 * src/scripts/popupModal.ts, research §7): the visitor is refusing an offer,
 * not admitting to anything. Its own constant, because the two controls are
 * different decisions on different screens and may one day read differently;
 * it lives here rather than beside the modal because kiroku-form.ts, the
 * html-mode renderer, does not bundle the modal at all.
 */
export const OUTCOME_DISMISS_LABEL = "Not right now";

/** Class name of the dismiss control, one string for three renderers. */
export const OUTCOME_DISMISS_CLASS = "kiroku-form-outcome-dismiss";

export function outcomeCtaHref(
  cta: FormOutcomeCta | null | undefined,
): string | null {
  const url = typeof cta?.url === "string" ? cta.url.trim() : "";
  const label = typeof cta?.label === "string" ? cta.label.trim() : "";
  // A url with no label renders a button nobody can read, so both are required.
  if (url === "" || label === "") return null;
  // Relative and in-page links stay on the page the form is embedded in.
  if (url.startsWith("/") || url.startsWith("#") || url.startsWith("?")) {
    return url;
  }
  if (SAFE_CTA_SCHEME.test(url)) return url;
  if (BARE_HOST.test(url)) return `https://${url}`;
  return null;
}

/**
 * Whether the result a visitor landed on asks for a person to look at it
 * (docs/conversational-forms_plan.md 6.2).
 *
 * Takes the form and the winning result's id rather than the outcome, because
 * `tallyOutcomes` returns a `QuizResult` and that widening deliberately drops
 * everything a renderer does not draw. `opensCase` is one of those: no
 * renderer shows it, only the server acts on it, and putting it on the result
 * would ship it to every customer's page for nothing.
 *
 * Strictly `true`. Everywhere else in this module a hand-edited settings blob
 * falls to the harmless reading, and here the harmless reading is "no": a case
 * spends a slot on the owner's concurrent rail (6.1) and lands in front of a
 * person, so `"yes"`, `1` and `"false"` must all mean no rather than yes.
 *
 * Pure, like the rest of this file. The impure half, which meters and creates,
 * is src/lib/services/outcome-case.ts, and it is separate so the three
 * renderers that import this module do not pull Prisma onto a customer's page.
 */
export function outcomeOpensCase(
  form: QuizFormLike | null | undefined,
  outcomeId: string | null | undefined,
): boolean {
  if (typeof outcomeId !== "string" || outcomeId === "") return false;
  const outcome = getFormOutcomes(form).find((o) => o.id === outcomeId);
  return outcome?.opensCase === true;
}

/** Widen a declared outcome into the shape a renderer switches on. */
function toResult(outcome: FormOutcome): QuizResult {
  const result: QuizResult = {
    type: "outcome",
    id: outcome.id,
    title: typeof outcome.title === "string" ? outcome.title : "",
    body: typeof outcome.body === "string" ? outcome.body : "",
  };
  const cta = outcome.cta;
  if (cta && typeof cta.label === "string" && typeof cta.url === "string") {
    result.cta = { label: cta.label, url: cta.url };
  }
  return result;
}

/**
 * Resolve a form's answers to the result the visitor sees.
 *
 * Returns `null` when the form declares no outcomes, which is every form
 * today: the caller then falls back to `settings.successMessage` exactly as
 * before. Never throws: a malformed settings blob loses its bad entries, not
 * the visitor's submission.
 *
 * Deterministic by construction, per decision 0.4: ties go to the earliest
 * outcome in author order, and when nothing scored at all the author's default
 * outcome answers (falling back to the first declared one if none is marked,
 * so a form can never reach the end with nothing to show).
 */
export function tallyOutcomes(
  form: QuizFormLike | null | undefined,
  answers: QuizAnswers | null | undefined,
): QuizResult | null {
  const outcomes = getFormOutcomes(form);
  if (outcomes.length === 0) return null;

  // Score bands take over the moment one outcome declares a usable range
  // (plan 5.2). The two models cannot be blended: on a scorecard every choice
  // is tagged toward the same result, so argmax would answer the same thing
  // for everybody who answered at all.
  const bands = scoreBands(form);
  if (bands.length > 0) {
    return toResult(bandWinner(bands, outcomes, totalScore(form, answers)));
  }

  const scores = tallyScores(form, answers);

  let winner = outcomes[0];
  let best = scores.get(winner.id) ?? 0;
  for (const outcome of outcomes.slice(1)) {
    const score = scores.get(outcome.id) ?? 0;
    if (score > best) {
      // Strictly greater, so an equal score leaves the earlier author-order
      // outcome in place. That is the tie-break, and it is why this loop must
      // never become `>=`.
      winner = outcome;
      best = score;
    }
  }

  if (best === 0) {
    return toResult(
      outcomes.find((outcome) => outcome.isDefault === true) ?? outcomes[0],
    );
  }

  return toResult(winner);
}
